import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

const RADIO_OPCOES = [
  { key: 'cobb_proximal', label: 'Cobb proximal' },
  { key: 'cobb_principal', label: 'Cobb principal' },
  { key: 'cobb_lombar', label: 'Cobb lombar' },
  { key: 'sva', label: 'SVA' },
  { key: 'csva', label: 'cSVA' },
  { key: 'lordose_cervical', label: 'Lordose cervical' },
  { key: 'lordose_lombar', label: 'Lordose lombar' },
  { key: 'cifose_t5_t12', label: 'Cifose T5-T12' },
  { key: 'deformidade_segmentar', label: 'Deformidade segmentar' },
  { key: 'slope_sacral', label: 'Slope sacral' },
  { key: 'pelvic_tilt', label: 'Pelvic tilt' },
  { key: 'incidencia_pelvica', label: 'Incidência pélvica' },
  { key: 'obliquidade_pelvica', label: 'Obliquidade pélvica' },
];

const MOMENTOS_PADRAO_SCORE = [
  { tipo: 'pre_op', label: 'Pré-operatório', dias_offset: null, ordem: 0, tipo_momento: 'score' },
  { tipo: 'pos_op_imediato', label: 'Pós-operatório imediato', dias_offset: 1, ordem: 1, tipo_momento: 'score' },
  { tipo: '3_meses', label: '3 Meses', dias_offset: 90, ordem: 2, tipo_momento: 'score' },
  { tipo: '6_meses', label: '6 Meses', dias_offset: 180, ordem: 3, tipo_momento: 'score' },
  { tipo: '1_ano', label: '1 Ano', dias_offset: 365, ordem: 4, tipo_momento: 'score' },
];

const MOMENTOS_PADRAO_RADIO = [
  { tipo: 'pre_op', label: 'Pré-operatório', dias_offset: null, ordem: 0, tipo_momento: 'radio' },
  { tipo: 'pos_op_imediato', label: 'Pós-operatório imediato', dias_offset: 1, ordem: 1, tipo_momento: 'radio' },
  { tipo: '3_meses', label: '3 Meses', dias_offset: 90, ordem: 2, tipo_momento: 'radio' },
  { tipo: '6_meses', label: '6 Meses', dias_offset: 180, ordem: 3, tipo_momento: 'radio' },
  { tipo: '1_ano', label: '1 Ano', dias_offset: 365, ordem: 4, tipo_momento: 'radio' },
];

const TIPOS_PREDEFINIDOS = [
  'ALIF', 'OLIF', 'TLIF', 'PLIF', 'Posterolateral',
  'Cervical Anterior Cage Bloqueado', 'Cervical Anterior Placa',
  'Cervical Posterior', 'Escoliose', 'Descompressão Isolada'
];

function ListaMomentos({ titulo, tipoMomento, momentos, pesquisaId, padroes, onRefresh, saving, setSaving }) {
  const [novo, setNovo] = useState({ label: '', dias_offset: '' });
  const lista = momentos.filter(m => m.tipo_momento === tipoMomento || m.tipo_momento === 'ambos');

  async function adicionarPadrao() {
    setSaving(true);
    for (const m of padroes) {
      const existe = lista.find(x => x.tipo === m.tipo);
      if (!existe) {
        await supabase.from('pesquisa_momentos').insert({ ...m, pesquisa_id: pesquisaId });
      }
    }
    onRefresh();
    setSaving(false);
  }

  async function adicionarCustom() {
    if (!novo.label) return;
    setSaving(true);
    await supabase.from('pesquisa_momentos').insert({
      pesquisa_id: pesquisaId,
      tipo: 'customizado',
      tipo_momento: tipoMomento,
      label: novo.label,
      dias_offset: novo.dias_offset ? Number(novo.dias_offset) : null,
      ordem: lista.length,
    });
    setNovo({ label: '', dias_offset: '' });
    onRefresh();
    setSaving(false);
  }

  async function remover(id) {
    await supabase.from('pesquisa_momentos').delete().eq('id', id);
    onRefresh();
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>{titulo}</h2>
        <button className="btn btn-sm btn-secondary" onClick={adicionarPadrao} disabled={saving}>
          + Padrão
        </button>
      </div>
      <div className="card-body">
        {lista.length === 0 ? (
          <div className="alert alert-info">Nenhum momento configurado. Clique em "+ Padrão" para adicionar o protocolo padrão.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {lista.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{m.label}</span>
                  {m.dias_offset && <span className="text-sm text-muted" style={{ marginLeft: 10 }}>({m.dias_offset} dias após cirurgia)</span>}
                  {m.tipo === 'customizado' && <span className="badge badge-blue" style={{ marginLeft: 8 }}>customizado</span>}
                  {m.tipo_momento === 'ambos' && <span className="badge badge-gray" style={{ marginLeft: 8 }}>legado</span>}
                </div>
                <button className="btn btn-sm btn-secondary" onClick={() => remover(m.id)} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>Remover</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="form-label">Novo momento customizado</label>
            <input className="form-input" placeholder="Ex: 2 Anos" value={novo.label} onChange={e => setNovo(n => ({ ...n, label: e.target.value }))} />
          </div>
          <div className="form-group" style={{ width: 160, marginBottom: 0 }}>
            <label className="form-label">Dias após cirurgia</label>
            <input className="form-input" type="number" placeholder="Ex: 730" value={novo.dias_offset} onChange={e => setNovo(n => ({ ...n, dias_offset: e.target.value }))} />
          </div>
          <button className="btn btn-secondary" onClick={adicionarCustom} disabled={!novo.label}>+ Adicionar</button>
        </div>
      </div>
    </div>
  );
}

export default function ConfigPesquisaPage() {
  const { isAdmin } = useAuth();
  const [pesquisas, setPesquisas] = useState([]);
  const [pesquisaSel, setPesquisaSel] = useState(null);
  const [momentos, setMomentos] = useState([]);
  const [parametros, setParametros] = useState(null);
  const [scores, setScores] = useState([]);
  const [scoresVinculados, setScoresVinculados] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [extraRadio, setExtraRadio] = useState('');
  const [tiposCirurgia, setTiposCirurgia] = useState([]);
  const [extraTipoInput, setExtraTipoInput] = useState('');
  const [confirmEncerrar, setConfirmEncerrar] = useState(false);

  useEffect(() => {
    supabase.from('pesquisas').select('id, titulo, tipos_cirurgia').eq('ativa', true).order('titulo')
      .then(({ data }) => setPesquisas(data || []));
    supabase.from('scores').select('id, nome, area').eq('ativo', true).order('nome')
      .then(({ data }) => setScores(data || []));
  }, []);

  async function selecionarPesquisa(p) {
    setPesquisaSel(p);
    setSaved('');
    setTiposCirurgia(p.tipos_cirurgia || []);
    setConfirmEncerrar(false);
    await recarregarMomentos(p.id);
    const [paramRes, scvRes] = await Promise.all([
      supabase.from('pesquisa_parametros_radio').select('*').eq('pesquisa_id', p.id).maybeSingle(),
      supabase.from('pesquisa_scores').select('score_id').eq('pesquisa_id', p.id),
    ]);
    setParametros(paramRes.data || {});
    setScoresVinculados((scvRes.data || []).map(s => s.score_id));
  }

  async function recarregarMomentos(id) {
    const { data } = await supabase.from('pesquisa_momentos').select('*').eq('pesquisa_id', id || pesquisaSel?.id).order('ordem');
    setMomentos(data || []);
  }

  async function salvarParametros() {
    setSaving(true);
    const extras = parametros?.extras || [];
    if (parametros?.id) {
      await supabase.from('pesquisa_parametros_radio').update({ ...parametros, extras }).eq('id', parametros.id);
    } else {
      await supabase.from('pesquisa_parametros_radio').insert({ ...parametros, pesquisa_id: pesquisaSel.id, extras });
    }
    setSaving(false);
    setSaved('radio');
    setTimeout(() => setSaved(''), 2000);
  }

  async function salvarScores() {
    setSaving(true);
    await supabase.from('pesquisa_scores').delete().eq('pesquisa_id', pesquisaSel.id);
    if (scoresVinculados.length > 0) {
      await supabase.from('pesquisa_scores').insert(scoresVinculados.map(score_id => ({ pesquisa_id: pesquisaSel.id, score_id })));
    }
    setSaving(false);
    setSaved('scores');
    setTimeout(() => setSaved(''), 2000);
  }

  function toggleScore(id) {
    setScoresVinculados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleRadio(key) {
    setParametros(p => ({ ...p, [key]: !p?.[key] }));
  }

  function adicionarExtra() {
    if (!extraRadio.trim()) return;
    setParametros(p => ({ ...p, extras: [...(p?.extras || []), extraRadio.trim()] }));
    setExtraRadio('');
  }

  function removerExtra(i) {
    setParametros(p => ({ ...p, extras: (p?.extras || []).filter((_, idx) => idx !== i) }));
  }

  function toggleTipoCirurgia(tipo) {
    setTiposCirurgia(prev => prev.includes(tipo) ? prev.filter(t => t !== tipo) : [...prev, tipo]);
  }

  function adicionarTipoExtra() {
    const val = extraTipoInput.trim();
    if (val && !tiposCirurgia.includes(val)) setTiposCirurgia(prev => [...prev, val]);
    setExtraTipoInput('');
  }

  function removerTipoExtra(tipo) {
    setTiposCirurgia(prev => prev.filter(t => t !== tipo));
  }

  async function salvarTiposCirurgia() {
    setSaving(true);
    await supabase.from('pesquisas').update({ tipos_cirurgia: tiposCirurgia.length > 0 ? tiposCirurgia : null }).eq('id', pesquisaSel.id);
    setSaving(false);
    setSaved('tipos');
    setTimeout(() => setSaved(''), 2000);
  }

  async function encerrarPesquisa() {
    setSaving(true);
    await supabase.from('pesquisas').update({ status: 'encerrada' }).eq('id', pesquisaSel.id);
    setSaving(false);
    setConfirmEncerrar(false);
    setSaved('encerrado');
    const { data } = await supabase.from('pesquisas').select('id, titulo, tipos_cirurgia').eq('ativa', true).order('titulo');
    setPesquisas(data || []);
  }

  if (!isAdmin) return (
    <div className="empty-state" style={{ marginTop: 60 }}>
      <div className="empty-icon">🔒</div>
      <h3>Acesso restrito</h3>
      <p>Apenas administradores podem configurar pesquisas.</p>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Configurar pesquisa</h1>
          <p>Defina momentos de avaliação, scores e parâmetros radiográficos</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <label className="form-label">Selecione a pesquisa</label>
          <select className="form-select" style={{ maxWidth: 480 }}
            value={pesquisaSel?.id || ''}
            onChange={e => { const p = pesquisas.find(x => x.id === e.target.value); if (p) selecionarPesquisa(p); }}>
            <option value="">Selecione...</option>
            {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
          </select>
        </div>
      </div>

      {pesquisaSel && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* TIPOS DE CIRURGIA */}
          <div className="card">
            <div className="card-header">
              <h2>Tipos de cirurgia</h2>
              <button className="btn btn-sm btn-primary" onClick={salvarTiposCirurgia} disabled={saving}>
                {saved === 'tipos' ? '✓ Salvo' : 'Salvar tipos de cirurgia'}
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {TIPOS_PREDEFINIDOS.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${tiposCirurgia.includes(t) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: tiposCirurgia.includes(t) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
                    <input type="checkbox" checked={tiposCirurgia.includes(t)} onChange={() => toggleTipoCirurgia(t)} style={{ accentColor: 'var(--blue)' }} />
                    {t}
                  </label>
                ))}
              </div>
              {tiposCirurgia.filter(t => !TIPOS_PREDEFINIDOS.includes(t)).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {tiposCirurgia.filter(t => !TIPOS_PREDEFINIDOS.includes(t)).map(e => (
                    <span key={e} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#f0f9ff', border: '1px solid var(--blue)', borderRadius: 20, fontSize: 13 }}>
                      {e}
                      <button onClick={() => removerTipoExtra(e)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, color: 'var(--text-2)' }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <input className="form-input" style={{ maxWidth: 280 }} placeholder="Tipo adicional..." value={extraTipoInput} onChange={e => setExtraTipoInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), adicionarTipoExtra())} />
                <button className="btn btn-secondary" onClick={adicionarTipoExtra} disabled={!extraTipoInput.trim()}>Adicionar</button>
              </div>
            </div>
          </div>

          {/* MOMENTOS DE SCORE */}
          <ListaMomentos
            titulo="Momentos de avaliação — Scores clínicos"
            tipoMomento="score"
            momentos={momentos}
            pesquisaId={pesquisaSel.id}
            padroes={MOMENTOS_PADRAO_SCORE}
            onRefresh={() => recarregarMomentos()}
            saving={saving}
            setSaving={setSaving}
          />

          {/* MOMENTOS DE RADIOGRAFIA */}
          <ListaMomentos
            titulo="Momentos de avaliação — Radiografia"
            tipoMomento="radio"
            momentos={momentos}
            pesquisaId={pesquisaSel.id}
            padroes={MOMENTOS_PADRAO_RADIO}
            onRefresh={() => recarregarMomentos()}
            saving={saving}
            setSaving={setSaving}
          />

          {/* SCORES */}
          <div className="card">
            <div className="card-header">
              <h2>Scores clínicos</h2>
              <button className="btn btn-sm btn-primary" onClick={salvarScores} disabled={saving}>
                {saved === 'scores' ? '✓ Salvo' : 'Salvar scores'}
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {scores.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: `1px solid ${scoresVinculados.includes(s.id) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: scoresVinculados.includes(s.id) ? '#eff6ff' : 'var(--surface)', transition: 'all .15s' }}>
                    <input type="checkbox" checked={scoresVinculados.includes(s.id)} onChange={() => toggleScore(s.id)} style={{ accentColor: 'var(--blue)', width: 16, height: 16 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.nome}</div>
                      <div className="text-sm text-muted">{s.area}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* PARÂMETROS RADIOGRÁFICOS */}
          <div className="card">
            <div className="card-header">
              <h2>Parâmetros radiográficos</h2>
              <button className="btn btn-sm btn-primary" onClick={salvarParametros} disabled={saving}>
                {saved === 'radio' ? '✓ Salvo' : 'Salvar parâmetros'}
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                {RADIO_OPCOES.map(({ key, label }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: `1px solid ${parametros?.[key] ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: parametros?.[key] ? '#eff6ff' : 'var(--surface)', transition: 'all .15s' }}>
                    <input type="checkbox" checked={!!parametros?.[key]} onChange={() => toggleRadio(key)} style={{ accentColor: 'var(--blue)', width: 16, height: 16 }} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
                  </label>
                ))}
              </div>
              <div className="form-label">Parâmetros adicionais</div>
              {(parametros?.extras || []).map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span className="badge badge-blue">{e}</span>
                  <button className="btn btn-sm btn-secondary" onClick={() => removerExtra(i)} style={{ color: 'var(--danger)' }}>×</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <input className="form-input" style={{ maxWidth: 280 }} placeholder="Nome do parâmetro extra" value={extraRadio} onChange={e => setExtraRadio(e.target.value)} onKeyDown={e => e.key === 'Enter' && adicionarExtra()} />
                <button className="btn btn-secondary" onClick={adicionarExtra} disabled={!extraRadio.trim()}>+ Adicionar</button>
              </div>
            </div>
          </div>

          {/* ENCERRAR PESQUISA */}
          <div className="card">
            <div className="card-header">
              <h2>Encerrar pesquisa</h2>
            </div>
            <div className="card-body">
              {saved === 'encerrado' ? (
                <div className="alert alert-info">Pesquisa encerrada com sucesso.</div>
              ) : !confirmEncerrar ? (
                <button className="btn btn-secondary" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => setConfirmEncerrar(true)}>
                  Encerrar pesquisa
                </button>
              ) : (
                <div>
                  <p className="text-sm" style={{ marginBottom: 14 }}>Tem certeza que deseja encerrar esta pesquisa? Esta ação não pode ser desfeita.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={() => setConfirmEncerrar(false)}>Cancelar</button>
                    <button className="btn" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }} onClick={encerrarPesquisa} disabled={saving}>
                      {saving ? 'Encerrando...' : 'Confirmar encerramento'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
